import { observer } from "mobx-react";
import { useEffect, useState } from "react";
import { EngagementPanel as EPanelStore } from "../../../api/engagementPanel";
import { useGlobalStore } from "../../../api/globalStores";
import AudioPlayerButton from "./AudioPlayerButton";
import MCQPicture from "./MCQPicture";
import MCQPollOption from "./MCQPollOption";
import { EngagementPanelResultString } from "../../../Common/Utility/helpers";
import BrijLoadingIndicator from "../../../Common/Misc/BrijLoadingIndicator";
import { useObjectUrl } from "../../../Common/Utility/useObjectUrl";

export default observer(EngagementPanel);

type Field = "A" | "B" | "C" | "D";

interface EngagementPanelProps {
  mode: "SETUP" | "ANSWERING" | "SHOW_ANSWER" | "MINI";
  isOnboarding?: boolean;
  onAnswerSelected?: (newAns: Field) => void;
  ePanel?: EPanelStore;
  curSelectedAnswer?: EngagementPanelResultString;
  setImagePreviewOption?: (option?: keyof EPanelStore["assets"]) => void;
  isAnswering?: boolean;
}

function EngagementPanel(props: EngagementPanelProps) {
  const globalStores = useGlobalStore();
  const curEPanel = props.ePanel ?? globalStores.curEPanel;

  const isLoadingAssets =
    curEPanel?.isAssetsLoading ||
    (!curEPanel?.engagementPanel?.id && globalStores.curModule.isLoading);

  return (
    <div
      className={`relative aspect-9/18 pt-10 ${
        props.mode === "SETUP" || props.mode === "MINI"
          ? "shadow-xl rounded-2xl"
          : ""
      } 
      ${
        props.mode === "MINI"
          ? "w-full h-full"
          : props.isOnboarding
          ? "h-full max-w-full"
          : "h-auto w-full sm:h-full sm:w-auto"
      } 
      bg-brij-White grid grid-cols`}
    >
      {isLoadingAssets && props.mode !== "MINI" && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70 backdrop-blur-sm">
          <BrijLoadingIndicator />
        </div>
      )}
      {curEPanel?.engagementPanel?.id ? (
        <>
          <EngagementPanelType
            engagementPanelType={curEPanel.engagementPanel.type ?? "MCQ"}
          />
          <QuestionText
            questionText={curEPanel.engagementPanel.qns}
            hasQuestionAsset={!!(curEPanel.assets.questionFile || curEPanel.assetUrls.question)}
          />
          <QuestionAsset
            key={curEPanel.engagementPanel.id}
            questionFile={curEPanel.assets.questionFile}
            questionUrl={curEPanel.assetUrls.question}
            ePanelType={curEPanel.engagementPanel.type}
            questionText={curEPanel.engagementPanel.qns}
            setImagePreviewOption={props.setImagePreviewOption}
          />
          <MCQOptions
            curSelectedAnswer={props.curSelectedAnswer}
            onAnswerSelected={props.onAnswerSelected}
            actualAnswer={curEPanel.engagementPanel.answer}
            mode={props.mode}
            assets={curEPanel.assets}
            assetUrls={curEPanel.assetUrls}
            ePanelType={curEPanel.engagementPanel.type}
            engagementPanel={curEPanel.engagementPanel}
            setImagePreviewOption={props.setImagePreviewOption}
            isAnswering={props.isAnswering}
          />
        </>
      ) : (
        <NoEPSelectedScreen />
      )}
    </div>
  );
}

const NoEPSelectedScreen = observer(() => {
  return (
    <div className="flex flex-col items-center justify-center w-full h-full p-8">
      <p className="font-poppins text-lg font-semibold text-center text-brij-DarkGrey inline leading-[1.5]">
        Waiting for Host Selection
      </p>
    </div>
  );
});

const EngagementPanelType = observer(
  ({ engagementPanelType }: { engagementPanelType?: string }) => {
    return (
      <div className="absolute top-3 inset-x-0 w-full h-auto flex justify-center">
        <div className="rounded-md bg-brij-Neutral p-1 px-1.5 flex flex-row items-center justify-center">
          <p className="font-poppins text-[10px] font-semibold text-brij-Mud leading-[1.5]">
            {engagementPanelType === "FLASH_CARD"
              ? "FLASHCARD"
              : engagementPanelType}
          </p>
        </div>
      </div>
    );
  }
);

const QuestionText = observer(
  ({
    questionText,
    hasQuestionAsset,
  }: {
    questionText?: string | null;
    hasQuestionAsset?: boolean;
  }) => {
    if (!questionText || questionText.trim() === "") return <></>;
    return (
      <div
        className={`flex items-center justify-center overflow-y-scroll p-3 w-full ${
          hasQuestionAsset ? "h-auto" : "h-full p-6"
        }`}
      >
        <div className="h-full w-full font-ePanel font-light text-2xl whitespace-pre-line">
          {questionText === "" || questionText === undefined
            ? "Enter Question Text"
            : questionText}
        </div>
      </div>
    );
  }
);

const QuestionAsset = observer(
  ({
    questionFile,
    questionUrl,
    ePanelType,
    questionText,
    setImagePreviewOption,
  }: {
    questionFile?: File;
    questionUrl?: string;
    ePanelType: EPanelStore["engagementPanel"]["type"];
    questionText?: string | null;
    setImagePreviewOption?: (option?: keyof EPanelStore["assets"]) => void;
  }) => {
    const objectUrl = useObjectUrl(questionFile);
    const fallbackUrl = questionUrl?.replace(/\.webp$/, "");
    const [srcState, setSrcState] = useState<"primary" | "fallback" | "audio">(
      "primary"
    );

    useEffect(() => {
      if (objectUrl) {
        setSrcState("primary");
      }
    }, [objectUrl]);

    const imgSrc =
      objectUrl ||
      (srcState === "primary" ? questionUrl : fallbackUrl);

    if (!questionUrl && !objectUrl) return <></>;

    if (srcState === "audio") {
      return (
        <div className="flex-1 overflow-hidden pt-4">
          <div className="h-full w-full flex justify-center items-center">
            <div className="w-28 h-28">
              <AudioPlayerButton file={questionFile} url={fallbackUrl} />
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="flex-1 overflow-hidden pt-4">
        <div className="relative w-full h-full overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center blur-xl opacity-45"
            style={{ backgroundImage: `url(${imgSrc})` }}
          />
          <img
            src={imgSrc}
            alt="question asset"
            decoding="async"
            className="relative object-contain w-full h-full drop-shadow-lg"
            onError={() => {
              if (srcState === "primary" && !objectUrl) {
                setSrcState("fallback");
              } else {
                setSrcState("audio");
              }
            }}
            onMouseEnter={
              setImagePreviewOption
                ? () => setImagePreviewOption("questionFile")
                : undefined
            }
            onMouseLeave={
              setImagePreviewOption
                ? () => setImagePreviewOption(undefined)
                : undefined
            }
          />
        </div>
      </div>
    );
  }
);

const MCQOptions = observer(
  ({
    curSelectedAnswer,
    onAnswerSelected,
    actualAnswer,
    mode,
    assets,
    assetUrls,
    ePanelType,
    engagementPanel,
    setImagePreviewOption,
    isAnswering,
  }: {
    onAnswerSelected: EngagementPanelProps["onAnswerSelected"];
    curSelectedAnswer: EngagementPanelProps["curSelectedAnswer"];
    actualAnswer: EPanelStore["engagementPanel"]["answer"];
    mode: EngagementPanelProps["mode"];
    assets: EPanelStore["assets"];
    assetUrls: EPanelStore["assetUrls"];
    ePanelType: EPanelStore["engagementPanel"]["type"];
    engagementPanel: EPanelStore["engagementPanel"];
    setImagePreviewOption?: (option?: keyof EPanelStore["assets"]) => void;
    isAnswering?: boolean;
  }) => {
    const getDisplayCorrect = (field: Field) => {
      if (ePanelType === "FLASH_CARD") {
        return false;
      }
      if (mode === "ANSWERING") {
        return curSelectedAnswer === field;
      } else if (mode === "SETUP" || mode === "SHOW_ANSWER") {
        return actualAnswer === field;
      }
      return false;
    };

    const getOnClick = (field: Field) => {
      if (mode === "ANSWERING" && ePanelType !== "FLASH_CARD" && !isAnswering) {
        return () => {
          onAnswerSelected?.(field);
        };
      }
    };

    if (ePanelType === "FLASH_CARD") {
      return <></>;
    }

    const ePanelIsPollOrMcq = ePanelType === "MCQ" || ePanelType === "POLL";
    const hasImageOptions = !!(
      assets.optionAFile || assets.optionBFile || assets.optionCFile || assets.optionDFile ||
      assetUrls.answerA || assetUrls.answerB || assetUrls.answerC || assetUrls.answerD
    );

    return (
      <div className="w-full box-border text-slate-600">
        {ePanelIsPollOrMcq && hasImageOptions ? (
          <MCQPictureOptionsLayout
            assets={assets}
            assetUrls={assetUrls}
            getDisplayCorrect={getDisplayCorrect}
            getOnClick={getOnClick}
            mode={mode}
            setImagePreviewOption={setImagePreviewOption}
          />
        ) : (
          <MCQPollOptionsLayout
            engagementPanel={engagementPanel}
            getDisplayCorrect={getDisplayCorrect}
            getOnClick={getOnClick}
            mode={mode}
          />
        )}
      </div>
    );
  }
);

const MCQPictureOptionsLayout = observer(
  ({
    assets,
    assetUrls,
    getDisplayCorrect,
    getOnClick,
    mode,
    setImagePreviewOption,
  }: {
    assets: EPanelStore["assets"];
    assetUrls: EPanelStore["assetUrls"];
    getDisplayCorrect: (field: Field) => boolean;
    getOnClick: (field: Field) => (() => void) | undefined;
    mode: EngagementPanelProps["mode"];
    setImagePreviewOption?: (option?: keyof EPanelStore["assets"]) => void;
  }) => {
    return (
      <div className="grid grid-cols-2 grid-rows-2 gap-3 p-4 w-full h-full box-border place-items-center">
        <MCQPicture
          optionName="A"
          answeringMode={mode === "ANSWERING"}
          correct={getDisplayCorrect("A")}
          onClick={getOnClick("A")}
          file={assets.optionAFile}
          url={assetUrls.answerA}
          onMouseEnter={
            setImagePreviewOption
              ? () => setImagePreviewOption("optionAFile")
              : undefined
          }
          onMouseLeave={
            setImagePreviewOption
              ? () => setImagePreviewOption(undefined)
              : undefined
          }
        />
        <MCQPicture
          optionName="B"
          answeringMode={mode === "ANSWERING"}
          correct={getDisplayCorrect("B")}
          onClick={getOnClick("B")}
          file={assets.optionBFile}
          url={assetUrls.answerB}
          onMouseEnter={
            setImagePreviewOption
              ? () => setImagePreviewOption("optionBFile")
              : undefined
          }
          onMouseLeave={
            setImagePreviewOption
              ? () => setImagePreviewOption(undefined)
              : undefined
          }
        />
        <MCQPicture
          optionName="C"
          answeringMode={mode === "ANSWERING"}
          correct={getDisplayCorrect("C")}
          onClick={getOnClick("C")}
          file={assets.optionCFile}
          url={assetUrls.answerC}
          onMouseEnter={
            setImagePreviewOption
              ? () => setImagePreviewOption("optionCFile")
              : undefined
          }
          onMouseLeave={
            setImagePreviewOption
              ? () => setImagePreviewOption(undefined)
              : undefined
          }
        />
        <MCQPicture
          optionName="D"
          answeringMode={mode === "ANSWERING"}
          correct={getDisplayCorrect("D")}
          onClick={getOnClick("D")}
          file={assets.optionDFile}
          url={assetUrls.answerD}
          onMouseEnter={
            setImagePreviewOption
              ? () => setImagePreviewOption("optionDFile")
              : undefined
          }
          onMouseLeave={
            setImagePreviewOption
              ? () => setImagePreviewOption(undefined)
              : undefined
          }
        />
      </div>
    );
  }
);

const MCQPollOptionsLayout = observer(
  ({
    getDisplayCorrect,
    getOnClick,
    mode,
    engagementPanel,
  }: {
    getDisplayCorrect: (field: Field) => boolean;
    getOnClick: (field: Field) => (() => void) | undefined;
    mode: EngagementPanelProps["mode"];
    engagementPanel: EPanelStore["engagementPanel"];
  }) => {
    return (
      <div className="grid grid-cols-1 grid-rows-4 gap-3 p-3 w-full h-auto box-border place-items-center">
        <MCQPollOption
          optionName="A"
          answeringMode={mode === "ANSWERING"}
          text={engagementPanel.optionA}
          type={engagementPanel.type!}
          correct={getDisplayCorrect("A")}
          onClick={getOnClick("A")}
        />
        <MCQPollOption
          optionName="B"
          answeringMode={mode === "ANSWERING"}
          text={engagementPanel.optionB}
          type={engagementPanel.type!}
          correct={getDisplayCorrect("B")}
          onClick={getOnClick("B")}
        />
        <MCQPollOption
          optionName="C"
          answeringMode={mode === "ANSWERING"}
          text={engagementPanel.optionC}
          type={engagementPanel.type!}
          correct={getDisplayCorrect("C")}
          onClick={getOnClick("C")}
        />
        <MCQPollOption
          optionName="D"
          answeringMode={mode === "ANSWERING"}
          text={engagementPanel.optionD}
          type={engagementPanel.type!}
          correct={getDisplayCorrect("D")}
          onClick={getOnClick("D")}
        />
      </div>
    );
  }
);
